/**
 * NOTE: For the Opportunity Details "Add/Edit Vehicle" flow, the inline
 * `YearMakeModelJSX`, `VINVehicleForm` and `InventoryVehicleForm` below have
 * been superseded by the dedicated modal in `./vehicleModal` (VehicleFormModal).
 * They are retained only because `WishlistVehicleForm` is still imported by
 * activityCenter/addLeadPhoneModal/addPhoneModal.js. Prefer the vehicleModal
 * feature for new work.
 */
import { useQuery, useLazyQuery } from "@apollo/client";
import { Form, Image, Input, InputNumber, Select } from "antd";
import { isEmpty } from "lodash";
import React, { useState, useEffect, useRef } from "react";
import {
  GET_LOCATION_VEHICLE_INVENTORY,
  GET_VEHICLE_MAKES,
  GET_VEHICLE_MODELS,
} from "../../graphql/query";
import { currencies } from "../../library/constants";
import { formatPrice, vehicleFilter } from "../../library/utils";
import VehicleInventoryDetailsDrawer from "../inventory/VehicleInventoryDetailsDrawer";
import VdpLocationSearch from "./activityCenter/VdpLocationSearch";

const { TextArea } = Input;

export function YearMakeModelJSX({
  InterestDropdown,
  index,
  item,
  years,
  DeleteIcon,
  setleadData,
  form,
}) {
  const currentVehicle = item || {};

  // Sync currentVehicle values with form - with loop prevention
  useEffect(() => {
    if (form) {
      const fields = {
        [`vehiclesOfInterest[${index}].year`]: currentVehicle?.year || undefined,
        [`vehiclesOfInterest[${index}].make`]: currentVehicle?.make || undefined,
        [`vehiclesOfInterest[${index}].model`]: currentVehicle?.model || undefined,
      };

      // Only update fields that are actually different
      const fieldsToUpdate = {};
      Object.keys(fields).forEach(fieldName => {
        const currentFormValue = form.getFieldValue(fieldName);
        const newValue = fields[fieldName];
        if (currentFormValue !== newValue) {
          fieldsToUpdate[fieldName] = newValue;
        }
      });

      if (Object.keys(fieldsToUpdate).length > 0) {
        form.setFieldsValue(fieldsToUpdate);
      }
    }
  }, [currentVehicle.year, currentVehicle.make, currentVehicle.model, form, index]);

  const [search, setsearch] = useState({
    make: "",
    model: "",
  });
  const [vehicleMakeModels, setvehicleMakeModels] = useState({
    makes: [],
    models: [],
  });

  const { loading: makeLoader } = useQuery(GET_VEHICLE_MAKES, {
    variables: {
      search: search.make,
      page: 1,
      pageSize: 20,
    },
    fetchPolicy: "network-only",
    onCompleted: res => {
      const makes = res?.getVehicleMakes?.data || [];
      const searchMake = (search.make || "").trim();
      const hasExactMatch = makes.some(
        make => (make.MakeName || "").toLowerCase() === searchMake.toLowerCase()
      );
      const shouldAppend = searchMake && !hasExactMatch && item.make !== searchMake;
      setvehicleMakeModels(prev => ({
        ...prev,
        makes: shouldAppend ? [{ MakeName: searchMake }, ...makes] : makes,
      }));
    },
    onError: () => {
      const searchMake = (search.make || "").trim();
      if (searchMake) {
        setvehicleMakeModels(prev => ({
          ...prev,
          makes: [{ MakeName: searchMake }],
        }));
      }
    },
  });

  const { loading: modelLoader } = useQuery(GET_VEHICLE_MODELS, {
    variables: {
      makeName: currentVehicle?.make,
      year: currentVehicle?.year,
      search: search.model,
    },
    skip: !currentVehicle?.make || !currentVehicle?.year,
    fetchPolicy: "network-only",
    onCompleted: res => {
      const models = res?.getVehicleModels?.data || [];
      const searchModel = (search.model || "").trim();
      const hasExactMatch = models.some(
        model => (model.ModelName || "").toLowerCase() === searchModel.toLowerCase()
      );
      const shouldAppend = searchModel && !hasExactMatch && item.model !== searchModel;
      setvehicleMakeModels(prev => ({
        ...prev,
        models: shouldAppend ? [{ ModelName: searchModel }, ...models] : models,
      }));
    },
    onError: () => {
      const searchModel = (search.model || "").trim();
      if (searchModel) {
        setvehicleMakeModels(prev => ({
          ...prev,
          models: [{ ModelName: searchModel }],
        }));
      }
    },
  });

  return (
    <div className="w-full gap-1 flex flex-row justify-between">
      {InterestDropdown}
      <Form.Item
        name={`vehiclesOfInterest[${index}].year`}
        rules={[{ required: true, message: "Please select Year" }]}
        className="w-[15%]"
        label="Year"
      >
        <Select
          className="vehicle-selector"
          placeholder="Year"
          onChange={value =>
            setleadData(prev => {
              const temp = { ...prev };
              temp.vehiclesOfInterest[index].year = value;
              return temp;
            })
          }
        >
          {years.map((item, index) => (
            <Select.Option key={index} value={item}>
              {item}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>
      <Form.Item
        name={`vehiclesOfInterest[${index}].make`}
        rules={[{ required: true, message: "Please select vehicle make" }]}
        className="w-[30%]"
        label="Make"
      >
        <Select
          showSearch
          loading={makeLoader}
          placeholder="Make"
          className="vehicle-selector"
          onSearch={value => setsearch({ ...search, make: value })}
          filterOption={false}
          onChange={value => {
            setleadData(prev => {
              const temp = { ...prev };
              temp.vehiclesOfInterest[index].make = value;
              return temp;
            });
          }}
          allowClear
          disabled={!currentVehicle?.year}
        >
          {(vehicleMakeModels?.makes || []).map((item, index) => (
            <Select.Option key={index} value={item.MakeName}>
              {item.MakeName}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>
      <Form.Item
        name={`vehiclesOfInterest[${index}].model`}
        rules={[{ required: true, message: "Please select vehicle model" }]}
        className="w-[50%]"
        label="Model"
      >
        <Select
          loading={modelLoader}
          showSearch
          placeholder="Model"
          className="vehicle-selector"
          disabled={!currentVehicle?.make}
          onSearch={value => setsearch({ ...search, model: value })}
          filterOption={false}
          onChange={value => {
            setleadData(prev => {
              const temp = { ...prev };
              temp.vehiclesOfInterest[index].model = value;
              return temp;
            });
          }}
          allowClear
        >
          {(vehicleMakeModels?.models || []).map((item, index) => (
            <Select.Option key={index} value={item.ModelName}>
              {item.ModelName}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>
      {DeleteIcon}
    </div>
  );
}

export function VINVehicleForm({ InterestDropdown, index, item, DeleteIcon, setleadData, form }) {
  const currentVehicle = item || {};

  // Sync currentVehicle values with form - with loop prevention
  useEffect(() => {
    if (form) {
      const fieldName = `vehiclesOfInterest[${index}].vin`;
      const currentFormValue = form.getFieldValue(fieldName);
      const newValue = currentVehicle?.vin || undefined;

      // Only update if the values are actually different
      if (currentFormValue !== newValue) {
        form.setFieldsValue({
          [fieldName]: newValue,
        });
      }
    }
  }, [currentVehicle.vin, form, index]);

  return (
    <div className="w-full gap-2 flex flex-row justify-between ">
      {InterestDropdown}
      <Form.Item
        name={`vehiclesOfInterest[${index}].vin`}
        className="w-full"
        rules={[{ required: true, message: "Please enter vin number" }]}
        label="VIN"
      >
        <Input
          className="vehicle-selector"
          onChange={e =>
            setleadData(prev => {
              const temp = { ...prev };
              temp.vehiclesOfInterest[index].vin = e.target.value;
              return temp;
            })
          }
          placeholder="Enter VIN number"
        />
      </Form.Item>
      {DeleteIcon}
    </div>
  );
}

export function InventoryVehicleForm({
  InterestDropdown,
  index,
  DeleteIcon,
  setleadData,
  leadData,
  form,
}) {
  const currentVehicle = leadData?.vehiclesOfInterest[index] || {};

  // Location selection + inventory fetch are colocated here (like the VDP
  // drawer): VdpLocationSearch emits the calculated location list, and we fetch
  // inventory for the checked, non-"All" locations it reports.
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [getVehicleInventory, { data: vdpData, loading: vehicleInventoryLoader }] = useLazyQuery(
    GET_LOCATION_VEHICLE_INVENTORY,
    { fetchPolicy: "network-only" }
  );
  const vehicleInventoryList = vdpData?.getVehicleInventoryV2?.data || [];

  useEffect(() => {
    const checkedLocationIds = (selectedLocations || [])
      .filter(loc => loc.isChecked && +loc.id !== 0)
      .map(loc => +loc.id);
    // VdpLocationSearch always reports the current location checked on init, so
    // wait until it has resolved a selection before fetching.
    if (!checkedLocationIds.length) return;
    getVehicleInventory({
      variables: { locationIds: checkedLocationIds, isAllLocations: false },
    });
  }, [selectedLocations, getVehicleInventory]);

  const [selectedVehicle, setselectedVehicle] = useState({});
  const [inventoryDetailsDrawerOpen, setInventoryDetailsDrawerOpen] = useState(false);
  const [selectedVehicleForInventoryDetails, setSelectedVehicleForInventoryDetails] =
    useState(null);
  // Controlled so the user's typed search and the dropdown open-state survive
  // the details drawer opening (which otherwise steals focus and wipes both).
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  // Set true when the user clicks "View" so the Select ignores the selection it
  // would otherwise trigger (View should only open the drawer, not pick the vehicle).
  const viewClickedRef = useRef(false);
  // Remembers whether the dropdown was open when "View" was clicked, so on drawer
  // close we only re-open the dropdown for a View from inside the list — not for a
  // View clicked on the already-selected value (dropdown closed).
  const reopenDropdownAfterDrawerRef = useRef(false);

  // Sync currentVehicle values with form - with loop prevention
  useEffect(() => {
    if (form) {
      const fieldName = `vehiclesOfInterest[${index}].inventoryId`;
      const currentFormValue = form.getFieldValue(fieldName);
      const newValue = currentVehicle?.inventoryId || undefined;

      // Only update if the values are actually different
      if (currentFormValue !== newValue) {
        form.setFieldsValue({
          [fieldName]: newValue,
        });
      }
    }
  }, [currentVehicle.inventoryId, form, index]);

  return (
    <>
      <div className="w-full gap-2 flex flex-row justify-between">
        <Form.Item
          rules={[{ required: true, message: "Please select location" }]}
          className="w-[20%]"
          label="Locations"
        >
          <VdpLocationSearch
            dropdownClassName="min-h-[40px]"
            onChange={setSelectedLocations}
            onLoadingChange={() => {}}
          />
        </Form.Item>

        {InterestDropdown}
        <Form.Item
          rules={[{ required: true, message: "Please select vehicle from inventory" }]}
          name={`vehiclesOfInterest[${index}].inventoryId`}
          className="w-full"
          label="Vehicle"
        >
          <Select
            showSearch
            open={isDropdownOpen}
            searchValue={searchValue}
            onSearch={setSearchValue}
            onDropdownVisibleChange={open => {
              // While the vehicle details drawer is open it steals focus and
              // triggers a close — ignore that so the dropdown & search text
              // stay intact. The drawer's onClose re-opens the dropdown.
              if (inventoryDetailsDrawerOpen && !open) return;
              setIsDropdownOpen(open);
            }}
            onChange={value => {
              // Ignore the selection caused by a "View" click
              if (viewClickedRef.current) {
                viewClickedRef.current = false;
                return;
              }
              setleadData(prev => {
                const temp = { ...prev };
                temp.vehiclesOfInterest[index].inventoryId = value;
                return temp;
              });
              setSearchValue("");
            }}
            size="large"
            className="vehicle-selector"
            placeholder={
              vehicleInventoryLoader ? "Loading vehicles..." : "Select vehicle from inventory"
            }
            loading={vehicleInventoryLoader}
            filterOption={(input, option) => vehicleFilter(input, option)}
            onSelect={value => {
              // Ignore the selection caused by a "View" click
              if (viewClickedRef.current) return;
              setselectedVehicle((vehicleInventoryList || []).find(item => +item.id === +value));
            }}
            style={{ height: "auto" }}
            dropdownStyle={{
              minWidth: "500px",
              height: "auto",
              maxHeight: "400px",
              overflowY: "auto",
            }}
          >
            {vehicleInventoryList.map((item, index) => (
              <Select.Option
                value={item.id}
                key={index}
                label={`${item.year || ""} ${item.make || ""} ${item.model || ""}`}
                vehicle={item}
                style={{ padding: "8px" }}
              >
                <div className="flex flex-row gap-2 items-center min-w-0">
                  <img
                    src={
                      (item?.vehicleInventoryMedias || [])[0]?.mediaUrl ||
                      "https://funnel-webapp-uploads.s3.amazonaws.com/prod/images/generic-car-not-available-image.png"
                    }
                    height="auto"
                    alt={`${item.year || ""} ${item.make || ""} ${item.model || ""}`}
                    width="40px"
                    className="rounded-full flex-shrink-0"
                  />
                  <div className="flex flex-col gap-0.5 flex-1 min-w-0 pr-2">
                    <div className="flex justify-between items-start gap-2">
                      <span className="text-sm font-[600] truncate">
                        {item.year || ""} {item.make || ""} {item.model || ""}
                      </span>
                      <span
                        className="text-[#1677FF] text-sm cursor-pointer hover:underline flex-shrink-0 whitespace-nowrap"
                        onMouseDown={e => {
                          // antd commits the option on mousedown (before click), so mark this
                          // as a View click; the Select's onChange/onSelect will ignore it.
                          // viewClickedRef.current = true;
                          e.stopPropagation();
                          e.preventDefault();
                        }}
                        onClick={e => {
                          e.stopPropagation();
                          // Only re-open the dropdown on drawer close if View was clicked
                          // from inside the open list (not from the selected value).
                          reopenDropdownAfterDrawerRef.current = isDropdownOpen;
                          setSelectedVehicleForInventoryDetails(item);
                          setInventoryDetailsDrawerOpen(true);
                        }}
                      >
                        View
                      </span>
                    </div>
                    <span className="text-sm text-grey-700 truncate">
                      <span className="font-semibold">MSRP:</span>{" "}
                      {item?.msrp || item?.sellingPrice
                        ? `${
                            currencies?.find(currency => currency.code === item.currencyUnit)
                              ?.symbol
                          } ${formatPrice(item?.msrp || item?.sellingPrice) || "N/A"}`
                        : "Price not available"}
                    </span>
                    <span className="text-xs font[400] text-[#64748B] truncate">{item.vin}</span>
                    <span className="text-sm text-grey-700 truncate">
                      <span className="font-semibold">Stock No</span>{" "}
                      <span>{item?.stockNumber || "N/A"}</span>
                    </span>
                    <span className="text-sm text-grey-700 truncate">
                      <span className="font-semibold">Model No</span>{" "}
                      <span>{item?.modelNumber || "N/A"}</span>
                    </span>
                    <span className="text-sm text-grey-700 truncate">
                      <span className="font-semibold">Color:</span>{" "}
                      <span>
                        {item?.exteriorColor || "N/A"}
                        <strong>(E)</strong>
                        {", "}
                      </span>
                      <span>
                        {item?.interiorColor || "N/A"}
                        <strong>(I)</strong>
                      </span>
                    </span>
                  </div>
                </div>
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        {!isEmpty(selectedVehicle) && (
          <div className="vehicle-images flex-row-centered items-center">
            <Image.PreviewGroup
              items={(selectedVehicle?.vehicleInventoryMedias || []).map(item => item.mediaUrl)}
            >
              <Image
                className="w-10 h-10 cursor-pointer "
                src={
                  selectedVehicle?.vehicleInventoryMedias[0]?.mediaUrl ||
                  "https://funnel-webapp-uploads.s3.amazonaws.com/prod/images/generic-car-not-available-image.png"
                }
              />
            </Image.PreviewGroup>
          </div>
        )}
        {DeleteIcon}
      </div>

      {/* Vehicle Inventory Details Drawer */}
      <VehicleInventoryDetailsDrawer
        open={inventoryDetailsDrawerOpen}
        onClose={() => {
          setInventoryDetailsDrawerOpen(false);
          setSelectedVehicleForInventoryDetails(null);
          // Re-open the dropdown only if View was launched from inside the open list;
          // if it was clicked on the selected value, keep the dropdown closed.
          setIsDropdownOpen(reopenDropdownAfterDrawerRef.current);
          reopenDropdownAfterDrawerRef.current = false;
        }}
        vehicleData={selectedVehicleForInventoryDetails}
      />
    </>
  );
}

export function WishlistVehicleForm({
  InterestDropdown,
  index,
  item,
  years,
  DeleteIcon,
  leadData,
  setleadData,
}) {
  const [search, setsearch] = useState({
    make: "",
    model: "",
  });
  const [vehicleMakeModels, setvehicleMakeModels] = useState({
    makes: [],
    models: [],
  });
  const { loading: makeLoader } = useQuery(GET_VEHICLE_MAKES, {
    variables: {
      search: search.make,
      page: 1,
      pageSize: 20,
    },
    onCompleted: res => {
      setvehicleMakeModels({ ...res, makes: res.getVehicleMakes.data });
    },
  });
  const { loading: modelLoader } = useQuery(GET_VEHICLE_MODELS, {
    variables: {
      makeName: leadData.vehiclesOfInterest[index].make,
      year: leadData.vehiclesOfInterest[index].year,
      search: search.model,
    },
    skip: !leadData.vehiclesOfInterest[index].make || !leadData.vehiclesOfInterest[index].year,
    onCompleted: res => {
      setvehicleMakeModels({ ...res, models: res.getVehicleModels.data });
    },
  });

  return (
    <div className="w-full border-[1px] border-grey-200 border-solid rounded-lg p-4">
      <div className="flex flex-row justify-between items-start mb-4">
        <h3 className="text-sm font-semibold">Wishlist Vehicle</h3>
        {DeleteIcon}
      </div>

      {/* Row 1: Condition, Year, Make, Model */}
      <div className="w-full gap-2 flex flex-row justify-between mb-2">
        <Form.Item
          name={`vehiclesOfInterest[${index}].condition`}
          rules={[{ required: true, message: "Required" }]}
          className="w-1/4"
          label="Condition"
        >
          <Select
            placeholder="Used/New"
            value={item.condition}
            onChange={value =>
              setleadData(prev => {
                const temp = { ...prev };
                temp.vehiclesOfInterest[index].condition = value;
                return temp;
              })
            }
          >
            <Select.Option value="new">New</Select.Option>
            <Select.Option value="used">Used</Select.Option>
          </Select>
        </Form.Item>

        <Form.Item
          name={`vehiclesOfInterest[${index}].year`}
          rules={[{ required: true, message: "Required" }]}
          className="w-1/4"
          label="Year"
        >
          <Select
            placeholder="Select"
            value={item.year}
            showSearch
            onChange={value =>
              setleadData(prev => {
                const temp = { ...prev };
                temp.vehiclesOfInterest[index].year = value;
                return temp;
              })
            }
          >
            {years.map((year, idx) => (
              <Select.Option key={idx} value={year}>
                {year}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name={`vehiclesOfInterest[${index}].make`}
          rules={[{ required: true, message: "Required" }]}
          className="w-1/4"
          label="Make"
        >
          <Select
            placeholder="Select"
            showSearch
            loading={makeLoader}
            onSearch={value => setsearch({ ...search, make: value })}
            onChange={value => {
              setleadData(prev => {
                const temp = { ...prev };
                temp.vehiclesOfInterest[index].make = value;
                return temp;
              });
            }}
            value={item.make}
            disabled={!item.year}
          >
            {(vehicleMakeModels?.makes || []).map((make, idx) => (
              <Select.Option key={idx} value={make.MakeName}>
                {make.MakeName}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name={`vehiclesOfInterest[${index}].model`}
          rules={[{ required: true, message: "Required" }]}
          className="w-1/4"
          label="Model"
        >
          <Select
            placeholder="Select"
            loading={modelLoader}
            showSearch
            disabled={!item.make}
            onSearch={value => setsearch({ ...search, model: value })}
            onChange={value => {
              setleadData(prev => {
                const temp = { ...prev };
                temp.vehiclesOfInterest[index].model = value;
                return temp;
              });
            }}
            value={item.model}
          >
            {(vehicleMakeModels?.models || []).map((model, idx) => (
              <Select.Option key={idx} value={model.ModelName}>
                {model.ModelName}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      </div>

      {/* Row 2: Trim, VIN, Exterior Color, Interior Color */}
      <div className="w-full gap-2 flex flex-row justify-between mb-2">
        <Form.Item name={`vehiclesOfInterest[${index}].trim`} className="w-1/4" label="Trim">
          <Input
            placeholder="Input"
            value={item.trim}
            onChange={e =>
              setleadData(prev => {
                const temp = { ...prev };
                temp.vehiclesOfInterest[index].trim = e.target.value;
                return temp;
              })
            }
          />
        </Form.Item>

        <Form.Item name={`vehiclesOfInterest[${index}].vin`} className="w-1/4" label="VIN">
          <Input
            placeholder="Input"
            value={item.vin}
            onChange={e =>
              setleadData(prev => {
                const temp = { ...prev };
                temp.vehiclesOfInterest[index].vin = e.target.value;
                return temp;
              })
            }
          />
        </Form.Item>

        <Form.Item
          name={`vehiclesOfInterest[${index}].exteriorColor`}
          className="w-1/4"
          label="Exterior Color"
        >
          <Input
            placeholder="Input"
            value={item.exteriorColor}
            onChange={e =>
              setleadData(prev => {
                const temp = { ...prev };
                temp.vehiclesOfInterest[index].exteriorColor = e.target.value;
                return temp;
              })
            }
          />
        </Form.Item>

        <Form.Item
          name={`vehiclesOfInterest[${index}].interiorColor`}
          className="w-1/4"
          label="Interior Color"
        >
          <Input
            placeholder="Input"
            value={item.interiorColor}
            onChange={e =>
              setleadData(prev => {
                const temp = { ...prev };
                temp.vehiclesOfInterest[index].interiorColor = e.target.value;
                return temp;
              })
            }
          />
        </Form.Item>
      </div>

      {/* Row 3: Body Style, Price, Mileage, Interest */}
      <div className="w-full gap-2 flex flex-row justify-between mb-2">
        <Form.Item
          name={`vehiclesOfInterest[${index}].bodyStyle`}
          className="w-1/4"
          label="Body Style"
        >
          <Select
            placeholder="Select"
            allowClear
            value={item.bodyStyle}
            onChange={value =>
              setleadData(prev => {
                const temp = { ...prev };
                temp.vehiclesOfInterest[index].bodyStyle = value;
                return temp;
              })
            }
          >
            <Select.Option value="Sedan">Sedan</Select.Option>
            <Select.Option value="SUV">SUV</Select.Option>
            <Select.Option value="Truck">Truck</Select.Option>
            <Select.Option value="Coupe">Coupe</Select.Option>
            <Select.Option value="Convertible">Convertible</Select.Option>
            <Select.Option value="Wagon">Wagon</Select.Option>
            <Select.Option value="Van">Van</Select.Option>
            <Select.Option value="Hatchback">Hatchback</Select.Option>
          </Select>
        </Form.Item>

        <Form.Item name={`vehiclesOfInterest[${index}].budget`} className="w-1/4" label="Price">
          <InputNumber
            prefix="$"
            placeholder="Input"
            className="w-full"
            value={item.budget}
            formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
            parser={value => value.replace(/\$\s?|(,*)/g, "")}
            onChange={value =>
              setleadData(prev => {
                const temp = { ...prev };
                temp.vehiclesOfInterest[index].budget = value;
                return temp;
              })
            }
          />
        </Form.Item>

        <Form.Item name={`vehiclesOfInterest[${index}].mileage`} className="w-1/4" label="Mileage">
          <InputNumber
            placeholder="Input"
            className="w-full"
            value={item.mileage}
            formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
            parser={value => value.replace(/\$\s?|(,*)/g, "")}
            onChange={value =>
              setleadData(prev => {
                const temp = { ...prev };
                temp.vehiclesOfInterest[index].mileage = value;
                return temp;
              })
            }
          />
        </Form.Item>

        {InterestDropdown}
      </div>

      {/* Row 4: Comments */}
      <div className="w-full">
        <Form.Item
          name={`vehiclesOfInterest[${index}].comments`}
          label="Comments"
          className="w-full"
        >
          <TextArea
            rows={2}
            placeholder="Textarea"
            value={item.comments}
            onChange={e =>
              setleadData(prev => {
                const temp = { ...prev };
                temp.vehiclesOfInterest[index].comments = e.target.value;
                return temp;
              })
            }
          />
        </Form.Item>
      </div>
    </div>
  );
}
